import { DateTime } from 'luxon';

import {
  ApjLicenseLabel,
  ApjTitleAbbreviation,
  ApjTitleEnum,
} from 'commons/enums';
import { ICompanyInfo, OrderType } from 'commons/types';
import { IPackingTask } from 'commons/types/packingTask.interface';

import {
  IInvoiceMappedData,
  IRenderedPickingItem,
} from '../../pages/pack/pack-print-page/interfaces';

const TABLE_CHUNK = 15;

export const mapInvoicePrintData = (
  packingTask: IPackingTask,
  company: ICompanyInfo | null,
  tableChunk: number = TABLE_CHUNK,
): IInvoiceMappedData => {
  const tableData: IRenderedPickingItem[][] = [];
  const items =
    packingTask.picking_task.items
      ?.map((item) => {
        const invoiceItems =
          packingTask.picking_task.invoice?.items[item.sku_code] || [];
        return invoiceItems.reduce<IRenderedPickingItem[]>(
          (res, invoiceItem) => [
            ...res,
            {
              sku_code: item.sku_code,
              name: item.name,
              quantity: invoiceItem.quantity,
              uom: item.uom || '',
              inventory: invoiceItem.batch_number || '',
              expiry_date: DateTime.fromISO(
                invoiceItem.expiration as unknown as string,
              ).toFormat('LL-yyyy'),
              price: invoiceItem.price,
              net_price: invoiceItem.net_price,
              discount: invoiceItem.discount,
              discount_rate: invoiceItem.discount_rate,
            },
          ],
          [],
        );
      })
      .flat() || [];
  for (let i = 0; i < items.length; i += tableChunk) {
    const chunk = items.slice(i, i + tableChunk);
    tableData.push(chunk);
  }
  const totalAmount = Math.floor(
    items.reduce((total, productInfo) => total + productInfo.net_price, 0),
  );

  const apjData =
    company?.apj[packingTask.picking_task?.po_type || OrderType.REGULAR];
  const orderVerificationDocument =
    packingTask.picking_task.order_verification_document;
  const apjAbbreviation = orderVerificationDocument?.metadata?.apj_title
    ? orderVerificationDocument.metadata.apj_title === ApjTitleEnum.APJ
      ? ApjTitleAbbreviation.APJ
      : orderVerificationDocument.metadata.apj_title === ApjTitleEnum.PJT
      ? ApjTitleAbbreviation.PJT
      : ApjTitleAbbreviation.APJ
    : ApjTitleAbbreviation.APJ;
  const invoiceDate =
    packingTask.picking_task.invoice?.invoiced_date ||
    packingTask.picking_task.createdAt;

  return {
    is_digital_signed: packingTask.picking_task.is_digital_signed || false,
    invoice_number: packingTask.picking_task.invoice?.invoice_number || '',
    invoice_date: DateTime.fromISO(
      packingTask.picking_task.invoice?.invoiced_date as unknown as string,
    ).toFormat('dd-LLL-yyyy'),
    po_number: packingTask.po_number,
    po_type: packingTask.picking_task?.po_type || OrderType.REGULAR,
    due_date: invoiceDate
      ? DateTime.fromISO(invoiceDate as unknown as string)
          .setZone('Asia/Jakarta')
          .plus({
            days: packingTask.picking_task.payment_terms_days_before_due || 30,
          })
          .toFormat('dd-LLL-yyyy')
      : '-',
    customer_id: packingTask?.picking_task?.pharmacy?.pt_id || null,
    customer_name:
      orderVerificationDocument?.metadata?.pharmacy_name ||
      packingTask?.picking_task?.pharmacy?.name ||
      '',
    customer_address: packingTask?.picking_task?.pharmacy?.address || '',
    items: tableData,
    is_copy_invoice:
      packingTask?.picking_task?.pharmacy?.is_copy_invoice || false,
    total_amount: totalAmount,
    total_amount_with_ppn:
      packingTask.picking_task.invoice?.invoiced_amount || 0,
    ppn: packingTask.picking_task.invoice?.tax_total || 0,
    verify_document_url: packingTask.picking_task.verify_document_url,
    company_info: company && {
      name: company.name,
      address: company.address,
      pbf_no: company.pbf_no,
      dak_no: company.dak_no,
      cdob_no: company.cdob_no,
      cdakb_no: company.cdakb_no,
      cdob_ccp_no: company.cdob_ccp_no || '',
      apj_signature: orderVerificationDocument?.is_historical
        ? orderVerificationDocument.metadata.apj_signature ||
          apjData?.signature ||
          ''
        : apjData?.signature || '',
      apj_name:
        orderVerificationDocument?.metadata?.apj_name || apjData?.name || '',
      apj_sipa:
        orderVerificationDocument?.metadata?.sipa_no || apjData?.sipa || '',
      apj_abbreviation: apjAbbreviation,
      apj_license_label:
        packingTask.picking_task?.po_type === OrderType.ALKES
          ? ApjLicenseLabel.IJAZAH
          : ApjLicenseLabel.STRA,
    },
  };
};
